<template>
  <div
    class="w-full h-auto p-6 bg-white rounded-xl shadow-md border border-slate-200"
  >
    <div class="mb-6">
      <label for="year-select" class="text-lg font-semibold text-gray-800 mx-2"
        >Select Year:</label
      >
      <select
        id="year-select"
        v-model="selectedYear"
        @change="fetchData"
        class="p-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option v-for="year in availableYears" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
    </div>

    <template v-if="dataLoaded">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center"
      >
        <div
          v-for="(chart, index) in charts"
          :key="index"
          class="w-full flex flex-col items-center bg-gray-50 rounded-xl shadow-md p-4"
        >
          <span class="text-center mb-4 text-xl font-medium text-gray-700">
            {{ chart.label }}
          </span>

          <div
            v-if="chart.label === 'Total Rewards'"
            class="flex flex-col items-center justify-center w-full h-40"
          >
            <p class="text-lg font-semibold text-[#2c963e]">
              Residential: ${{ chartData.totalRewards.residential }}
            </p>
            <p class="text-lg font-semibold text-[#00b2e4]">
              Commercial: ${{ chartData.totalRewards.commercial }}
            </p>
            <p class="text-lg font-semibold text-gray-700">
              Total: ${{ chartData.totalRewards.total }}
            </p>
            <button
              class="px-5 py-3 mt-5 rounded-xl shadow-lg border border-slate-300 justify-center items-center gap-2 flex bg-blue_primary"
            >
              <a
                href="https://ziplyfiberrewards.awardco.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="text-white text-lg font-semibold leading-snug"
              >
                Go to awardco
              </a>
            </button>
          </div>

          <div
            v-else
            class="chart-container w-full h-80 bg-white rounded-lg shadow-sm"
          >
            <component
              :is="chart.component"
              :data="chart.data"
              :options="chartOptions"
            />
          </div>
        </div>
      </div>
    </template>

    <ZLoader v-else></ZLoader>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { Doughnut, Bar, Line } from "vue-chartjs";
import { onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import ZLoader from "./ZLoader.vue";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

export default {
  components: {
    Doughnut,
    Bar,
    Line,
    ZLoader,
  },
  setup() {
    const store = useStore();
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    const dataLoaded = ref(false);
    const chartOptions = { responsive: true, maintainAspectRatio: false };
    const selectedYear = ref("");
    const availableYears = ref([]);

    const chartData = reactive({
      leadsByCategory: {
        labels: ["Residential", "Commercial"],
        datasets: [{ backgroundColor: ["#2c963e", "#00b2e4"], data: [] }],
      },
      leadsByMonth: {
        labels: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        datasets: [
          {
            label: "Residential",
            backgroundColor: "#2c963e",
            data: Array(12).fill(0),
          },
          {
            label: "Commercial",
            backgroundColor: "#00b2e4",
            data: Array(12).fill(0),
          },
        ],
      },
      rewardsByMonth: {
        labels: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        datasets: [
          {
            label: "Residential",
            backgroundColor: "#2c963e",
            data: Array(12).fill(0),
          },
          {
            label: "Commercial",
            backgroundColor: "#00b2e4",
            data: Array(12).fill(0),
          },
        ],
      },
      leadsByStatus: {
        labels: [],
        datasets: [
          { label: "Residential", backgroundColor: "#2c963e", data: [] },
          { label: "Commercial", backgroundColor: "#00b2e4", data: [] },
        ],
      },
      participationByMonth: {
        labels: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        datasets: [
          {
            label: "Residential",
            backgroundColor: "#2c963e",
            data: Array(12).fill(0),
          },
          {
            label: "Commercial",
            backgroundColor: "#00b2e4",
            data: Array(12).fill(0),
          },
        ],
      },
      totalRewards: {
        residential: 0,
        commercial: 0,
        total: 0,
      },
    });

    const charts = [
      {
        label: "Leads by Category",
        component: Doughnut,
        data: chartData.leadsByCategory,
      },
      {
        label: "Leads by Month",
        component: Line,
        data: chartData.leadsByMonth,
      },
      {
        label: "Rewards by Month",
        component: Bar,
        data: chartData.rewardsByMonth,
      },
      {
        label: "Leads by Status",
        component: Bar,
        data: chartData.leadsByStatus,
      },
      {
        label: "Participation % By Month",
        component: Bar,
        data: chartData.participationByMonth,
      },
      {
        label: "Total Rewards",
        component: "div",
        data: {
          residential: chartData.totalRewards.residential,
          commercial: chartData.totalRewards.commercial,
          total: chartData.totalRewards.total,
        },
      },
    ];

    onMounted(() => {
      loadAvailableYears();
      fetchData();
    });

    function loadAvailableYears() {
      const currentYear = new Date().getFullYear();
      availableYears.value = Array.from(
        { length: currentYear - 2021 + 1 },
        (_, i) => 2021 + i
      );
      selectedYear.value = currentYear;
    }

    async function fetchData() {
      dataLoaded.value = false;
      try {
        const responses = await Promise.all([
          store.dispatch("getLeadsByCategoryKPI", {
            submitter_email_contact: userData.email,
            year: selectedYear.value,
          }),
          store.dispatch("getLeadsByMonthKPI", {
            submitter_email_contact: userData.email,
            year: selectedYear.value,
          }),
          store.dispatch("getRewardsByMonthKPI", {
            submitter_email_contact: userData.email,
            year: selectedYear.value,
          }),
          store.dispatch("getLeadsByStatusKPI", {
            submitter_email_contact: userData.email,
            year: selectedYear.value,
          }),
          store.dispatch("getParticipationByMonthKPI", {
            submitter_email_contact: userData.email,
            year: selectedYear.value,
          }),
        ]);

        populateLeadsByCategory(responses[0]);
        populateLeadsByMonth(responses[1]);
        populateRewardsByMonth(responses[2]);
        populateLeadsByStatus(responses[3]);
        populateParticipationByMonth(responses[4]);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      } finally {
        dataLoaded.value = true;
      }
    }

    function populateLeadsByCategory(response) {
      chartData.leadsByCategory.datasets[0].data = [
        response.residential.totalItems || 0,
        response.commercial.totalItems || 0,
      ];
    }

    function populateLeadsByMonth(response) {
      resetMonthlyData(chartData.leadsByMonth.datasets);
      response.residential.forEach(
        (entry) =>
          (chartData.leadsByMonth.datasets[0].data[entry.month - 1] =
            entry.count)
      );
      response.commercial.forEach(
        (entry) =>
          (chartData.leadsByMonth.datasets[1].data[entry.month - 1] =
            entry.count)
      );
    }

    function populateRewardsByMonth(response) {
      resetMonthlyData(chartData.rewardsByMonth.datasets);
      chartData.rewardsByMonth.datasets[0].data = response.residential;
      chartData.rewardsByMonth.datasets[1].data = response.commercial;

      chartData.totalRewards.residential = response.residential.reduce(
        (a, b) => a + b,
        0
      );
      chartData.totalRewards.commercial = response.commercial.reduce(
        (a, b) => a + b,
        0
      );
      chartData.totalRewards.total =
        chartData.totalRewards.residential + chartData.totalRewards.commercial;
    }
    function populateLeadsByStatus(response) {
      const uniqueStatuses = new Set([
        ...response.residential.map((item) => item.status || "Unknown"),
        ...response.commercial.map((item) => item.status || "Unknown"),
      ]);
      chartData.leadsByStatus.labels = Array.from(uniqueStatuses);

      chartData.leadsByStatus.datasets[0].data = Array(
        uniqueStatuses.size
      ).fill(0);
      chartData.leadsByStatus.datasets[1].data = Array(
        uniqueStatuses.size
      ).fill(0);

      response.residential.forEach((item) => {
        const index = chartData.leadsByStatus.labels.indexOf(
          item.status || "Unknown"
        );
        chartData.leadsByStatus.datasets[0].data[index] = item.count;
      });

      response.commercial.forEach((item) => {
        const index = chartData.leadsByStatus.labels.indexOf(
          item.status || "Unknown"
        );
        chartData.leadsByStatus.datasets[1].data[index] = item.count;
      });
    }
    function populateParticipationByMonth(response) {
      resetMonthlyData(chartData.participationByMonth.datasets);

      response.residential.forEach((entry) => {
        const monthIndex = parseInt(entry.month, 10) - 1;
        chartData.participationByMonth.datasets[0].data[monthIndex] =
          entry.percentage;
      });

      response.commercial.forEach((entry) => {
        const monthIndex = parseInt(entry.month, 10) - 1;
        chartData.participationByMonth.datasets[1].data[monthIndex] =
          entry.percentage;
      });
    }

    function resetMonthlyData(datasets) {
      datasets.forEach((dataset) => (dataset.data = Array(12).fill(0)));
    }

    return {
      charts,
      chartOptions,
      dataLoaded,
      selectedYear,
      fetchData,
      availableYears,
      chartData,
    };
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  max-width: 600px;
  height: 300px;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .chart-container {
    width: 90%;
  }
}
</style>
